import React, { useState, useEffect, useRef } from 'react';
import { Calculator } from './components/Calculator';
import { Header } from './components/Header';
import { CalculatorResults } from './components/CalculatorResults';
import { ContactCTA } from './components/ContactCTA';
import { LoadingOverlay } from './components/LoadingOverlay';
import { Modal } from './components/Modal';
import { Toast } from './components/Toast';
import { ResultsCTA } from './components/ResultsCTA';
import { DebugPanel } from './components/DebugPanel';
import { StickyHeader } from './components/StickyHeader';
import { ArrowLeft } from 'lucide-react';
import type { CalculationResults } from './types';
import { logger } from './utils/logging';
import { subscribeToApiCalls, type ApiCall, isDevMode } from './utils/api';

interface ToastState {
  message: string;
  type: 'success' | 'error';
}

function App() {
  const [results, setResults] = useState<CalculationResults | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
  const [showDebugPanel, setShowDebugPanel] = useState(true);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const showDevTools = isDevMode();

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current && showResults) {
        const headerBottom = headerRef.current.getBoundingClientRect().bottom;
        setShowStickyHeader(headerBottom < 0);
      } else {
        setShowStickyHeader(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showResults]);

  useEffect(() => {
    if (!showDevTools) return;

    const unsubscribe = subscribeToApiCalls((call) => {
      setApiCalls(prev => [...prev, call]);
    });

    return () => unsubscribe();
  }, [showDevTools]);

  const handleCalculate = (res: CalculationResults | null, loading: boolean) => {
    logger.info('Calculation state updated', { hasResults: !!res, loading });
    setResults(res);
    setIsCalculating(loading);
    if (res) {
      setShowResults(true);
      setShowStickyHeader(false); // Reset sticky header when showing results
    }
  };

  const handleSkipLoading = () => {
    logger.info('Loading skipped');
    setIsCalculating(false);
    if (results) {
      setShowResults(true);
    }
  };

  const handleBack = () => {
    logger.info('Navigating back to calculator');
    setShowResults(false);
    setShowStickyHeader(false); // Hide sticky header when going back
  };

  const handleContact = () => {
    logger.info('Opening contact modal');
    setShowContactModal(true);
  };

  const handleContactSuccess = () => {
    logger.info('Contact form submitted successfully');
    setShowContactModal(false);
    setToast({
      message: 'Thank you! We will contact you within 24 hours.',
      type: 'success'
    });
  };

  const handleContactError = () => {
    logger.error('Contact form submission failed');
    setToast({
      message: 'Sorry, there was an error. Please try again.',
      type: 'error'
    });
  };

  const handleCloseDebugPanel = () => {
    logger.info('Debug panel closed');
    setShowDebugPanel(false);
    setApiCalls([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {showResults && <StickyHeader visible={showStickyHeader} onContact={handleContact} />}
      
      {isCalculating && <LoadingOverlay onSkip={handleSkipLoading} />}
      
      <div ref={headerRef}>
        <Header />
      </div>
      
      <main className="container mx-auto px-4 py-8">
        {!showResults ? (
          <section className="max-w-3xl mx-auto">
            <Calculator onCalculate={handleCalculate} />
          </section>
        ) : (
          <div className="space-y-8">
            <div className="flex items-center">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                Back to Calculator
              </button>
            </div>
            
            <section className="bg-white rounded-xl shadow-lg overflow-hidden">
              <CalculatorResults results={results} isLoading={isCalculating} />
            </section>

            <section>
              <ResultsCTA onContact={handleContact} />
            </section>
          </div>
        )}
      </main>

      <Modal isOpen={showContactModal} onClose={() => setShowContactModal(false)}>
        <ContactCTA
          onSuccess={handleContactSuccess}
          onError={handleContactError}
        />
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showDevTools && showDebugPanel && (
        <DebugPanel 
          apiCalls={apiCalls}
          onClose={handleCloseDebugPanel}
        />
      )}
    </div>
  );
}

export default App;